import React, {useEffect} from 'react';

  import {useApi} from "../../../providers/ApiProvider";
  import BusinessesModel from "../../../models/BusinessesModel";
  import {Link, useParams} from "react-router-dom";
  import {useBreadcrumbs} from "../../../providers/BreadcrumbsProvider";
  import BusinessViewHeader from "./../BusinessViewHeader";
  import BusinessViewMenu from "../BusinessViewMenu";
  import {useTranslation} from "react-i18next";
  import {useTranslation as useMyTranslation} from "../../../providers/TranslationProvider";
  import BusinessCompetitorCard from "./BusinessCompetitorCard";
  import StarRating from "../../ui/StarRating";
  import Chart from "react-apexcharts";
  import ButtonWithLoading from "../../layout/ui/ButtonWithLoading";

  function BusinessCompetitors({}) {
      const {language} = useMyTranslation();
      const { t} = useTranslation();
      const params = useParams();
      const apiContext = useApi();
      const businessesModel = new BusinessesModel(apiContext.api);

      const breadcrumbs = useBreadcrumbs();
      const [businessProfile, setBusinessProfile] = React.useState(null);
      const [competitors, setCompetitors] = React.useState([]);
      const [loading, setLoading] = React.useState(false);
      const [viewMode, setViewMode] = React.useState('comparison');
      const [trends, setTrends] = React.useState([]);
      const [trendsLoading, setTrendsLoading] = React.useState(false);
      const [analysis, setAnalysis] = React.useState(null);
      const [analysisLoading, setAnalysisLoading] = React.useState(false);


      const getBusinessRating = () => {
          if (!businessProfile) return 0;
          return businessProfile.avgRating || businessProfile.rating || 0;
      }

      const getBusinessReviewCount = () => {
          if (!businessProfile) return 0;
          return businessProfile.totalReviews || businessProfile.userRatingCount || 0;
      }

      const getBusinessTitle = () => {
          if (!businessProfile) return t('Ihr Unternehmen');
          return businessProfile.title || t('Ihr Unternehmen');
      }

      const getBusinessAddress = () => {
          if (!businessProfile) return '-';
          if (businessProfile.storefrontAddress) {
              let parts = [];
              if (businessProfile.storefrontAddress.addressLines) parts.push(businessProfile.storefrontAddress.addressLines.join(', '));
              if (businessProfile.storefrontAddress.postalCode) parts.push(businessProfile.storefrontAddress.postalCode);
              if (businessProfile.storefrontAddress.locality) parts.push(businessProfile.storefrontAddress.locality);
              return parts.join(', ') || '-';
          }
          if (businessProfile.formattedAddress) return businessProfile.formattedAddress;
          return '-';
      }

      const getBusinessWebsite = () => {
          if (!businessProfile) return null;
          return businessProfile.websiteUri || businessProfile.website || null;
      }

      const getBusinessPhone = () => {
          if (!businessProfile) return null;
          return businessProfile.phoneNumbers?.primaryPhone || businessProfile.internationalPhoneNumber || null;
      }


      const refreshCompetitors = () => {
          setLoading(true);
          businessesModel.listCompetitors(params.locationId).then((response) => {
              if (response !== false) {
                  setCompetitors(response);
              }
          }).finally(() => {
              setLoading(false);
          });
      }


      const loadTrends = () => {
          setTrendsLoading(true);
          businessesModel.getCompetitorsTrends(params.locationId).then((response) => {
              if (response !== false) {
                  setTrends(response);
              }
          }).finally(() => {
              setTrendsLoading(false);
          });
      }


      const loadAnalysis = () => {
          setAnalysisLoading(true);
          setAnalysis(null);
          businessesModel.analyzeCompetitors(params.locationId).then((response) => {
              if (response !== false) {
                  if (response.parsed && response.analysis) {
                      setAnalysis(response.analysis);
                  } else if (response.raw) {
                      try {
                          const parsed = JSON.parse(response.raw);
                          setAnalysis(parsed);
                      } catch(e) {
                          setAnalysis({ summary: response.raw, strengths: [], weaknesses: [], opportunities: [], recommendations: [], competitorInsights: [] });
                      }
                  } else if (typeof response === 'string') {
                      try {
                          setAnalysis(JSON.parse(response));
                      } catch(e) {
                          setAnalysis({ summary: response, strengths: [], weaknesses: [], opportunities: [], recommendations: [], competitorInsights: [] });
                      }
                  }
              }
          }).catch((err) => {
              setAnalysis({ summary: t('Fehler bei der Analyse. Bitte versuchen Sie es erneut.'), strengths: [], weaknesses: [], opportunities: [], recommendations: [], competitorInsights: [] });
          }).finally(() => {
              setAnalysisLoading(false);
          });
      }


      const setMetaData = (profile) => {
          if (profile !== false) {
              const title = profile.title || '';
              breadcrumbs.update({
                  title: title,
                  breadcrumbs: [
                      {label: t('Businesses List'), url: '/businesses'},
                      {label: title, url: '/businesses/'+params.locationId},
                      {label: t('Competitors')}
                  ]
              });
              document.title = t('Competitors')+' | '+title+' | LocalBoost';
          }
      }


      const onProfileRefresh = (profile, lastUpdated) => {
          setMetaData(profile);
          setBusinessProfile(profile);
          refreshCompetitors();
      }


      const onCompetitorDelete = (competitorId) => {
          businessesModel.deleteCompetitor(params.locationId, competitorId).then((response) => {
              if (response !== false) {
                  refreshCompetitors();
              }
          });
      }


      useEffect(() => {
          businessesModel.view(params.locationId).then((response) => {
              if (response !== false) {
                  setBusinessProfile(response.data);
                  setMetaData(response.data);
                  refreshCompetitors();
              }
          });
      }, [language]);


      useEffect(() => {
          if (viewMode === 'trends' && trends.length === 0 && !trendsLoading) {
              loadTrends();
          }
      }, [viewMode]);


      const getRatingColor = (rating) => {
          if (rating >= 4.5) return '#28a745';
          if (rating >= 4.0) return '#5D87FF';
          if (rating >= 3.0) return '#ffc107';
          return '#dc3545';
      }

      const getThreatColor = (threat) => {
          if (threat === 'hoch') return 'danger';
          if (threat === 'mittel') return 'warning';
          return 'success';
      }


      const getRatingChartOptions = () => {
          if (!businessProfile || !competitors.length) return null;

          const myRating = getBusinessRating();
          const allEntries = [
              { name: getBusinessTitle(), rating: myRating, reviews: getBusinessReviewCount(), isOwn: true },
              ...competitors.map(c => ({
                  name: c.name || t('Unbekannt'),
                  rating: c.data?.rating || 0,
                  reviews: c.data?.userRatingCount || 0,
                  isOwn: false
              }))
          ];
          allEntries.sort((a, b) => b.rating - a.rating);

          return {
              options: {
                  chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
                  plotOptions: { bar: { horizontal: true, barHeight: '60%', borderRadius: 4, distributed: true } },
                  colors: allEntries.map(e => e.isOwn ? '#6D2AF6' : getRatingColor(e.rating)),
                  dataLabels: { enabled: true, formatter: function(val) { return val.toFixed(1); }, style: { fontSize: '13px', fontWeight: 600, colors: ['#fff'] } },
                  xaxis: { categories: allEntries.map(e => e.isOwn ? e.name + ' \u2B50' : e.name), max: 5, min: 0, labels: { style: { fontSize: '12px' } } },
                  yaxis: { labels: { style: { fontSize: '12px' } } },
                  legend: { show: false },
                  tooltip: { y: { formatter: function(val, opts) { const entry = allEntries[opts.dataPointIndex]; return val.toFixed(1) + ' (' + entry.reviews + ' Bewertungen)'; } } },
                  grid: { borderColor: '#f1f1f1' }
              },
              series: [{ name: 'Bewertung', data: allEntries.map(e => e.rating) }]
          };
      }


      const getReviewsChartOptions = () => {
          if (!businessProfile || !competitors.length) return null;

          const allEntries = [
              { name: getBusinessTitle(), reviews: getBusinessReviewCount(), isOwn: true },
              ...competitors.map(c => ({ name: c.name || t('Unbekannt'), reviews: c.data?.userRatingCount || 0, isOwn: false }))
          ];
          allEntries.sort((a, b) => b.reviews - a.reviews);

          return {
              options: {
                  chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
                  plotOptions: { bar: { horizontal: true, barHeight: '60%', borderRadius: 4, distributed: true } },
                  colors: allEntries.map(e => e.isOwn ? '#6D2AF6' : '#5D87FF'),
                  dataLabels: { enabled: true, style: { fontSize: '13px', fontWeight: 600, colors: ['#fff'] } },
                  xaxis: { categories: allEntries.map(e => e.isOwn ? e.name + ' \u2B50' : e.name), labels: { style: { fontSize: '12px' } } },
                  yaxis: { labels: { style: { fontSize: '12px' } } },
                  legend: { show: false },
                  grid: { borderColor: '#f1f1f1' }
              },
              series: [{ name: 'Bewertungen', data: allEntries.map(e => e.reviews) }]
          };
      }


      const getTrendsRatingChart = () => {
          if (!trends.length) return null;
          const series = trends.filter(t => t.ratingHistory?.length > 0).map(t => ({
              name: t.name,
              data: t.ratingHistory.map(h => [new Date(h.date).getTime(), h.value])
          }));
          if (!series.length) return null;

          return {
              options: {
                  chart: { type: 'line', toolbar: { show: true }, fontFamily: 'inherit', zoom: { enabled: true } },
                  stroke: { curve: 'smooth', width: 2 },
                  xaxis: { type: 'datetime', labels: { style: { fontSize: '11px' } } },
                  yaxis: { min: 0, max: 5, decimalsInFloat: 1, labels: { formatter: (val) => val.toFixed(1) } },
                  legend: { position: 'top', fontSize: '12px' },
                  tooltip: { x: { format: 'dd.MM.yyyy' }, y: { formatter: (val) => val.toFixed(1) } },
                  grid: { borderColor: '#f1f1f1' },
                  colors: ['#6D2AF6', '#5D87FF', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#fd7e14', '#6f42c1']
              },
              series: series
          };
      }


      const getTrendsReviewsChart = () => {
          if (!trends.length) return null;
          const series = trends.filter(t => t.reviewsHistory?.length > 0).map(t => ({
              name: t.name,
              data: t.reviewsHistory.map(h => [new Date(h.date).getTime(), h.value])
          }));
          if (!series.length) return null;

          return {
              options: {
                  chart: { type: 'line', toolbar: { show: true }, fontFamily: 'inherit', zoom: { enabled: true } },
                  stroke: { curve: 'smooth', width: 2 },
                  xaxis: { type: 'datetime', labels: { style: { fontSize: '11px' } } },
                  yaxis: { labels: { formatter: (val) => Math.round(val).toString() } },
                  legend: { position: 'top', fontSize: '12px' },
                  tooltip: { x: { format: 'dd.MM.yyyy' }, y: { formatter: (val) => Math.round(val).toString() } },
                  grid: { borderColor: '#f1f1f1' },
                  colors: ['#6D2AF6', '#5D87FF', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#fd7e14', '#6f42c1']
              },
              series: series
          };
      }


      const ratingChart = getRatingChartOptions();
      const reviewsChart = getReviewsChartOptions();
      const chartHeight = Math.max(250, (1 + competitors.length) * 50);
      const trendsRatingChart = getTrendsRatingChart();
      const trendsReviewsChart = getTrendsReviewsChart();


      return (
          <>
              <BusinessViewHeader locationId={params.locationId} onProfileRefresh={onProfileRefresh} />
              <BusinessViewMenu locationId={params.locationId} active="competitors" />

              <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="btn-group" role="group">
                      <button className={"btn btn-sm " + (viewMode === 'comparison' ? 'btn-primary' : 'btn-outline-primary')} onClick={() => setViewMode('comparison')}>
                          <i className="ti ti-chart-bar me-1"></i> {t('Vergleich')}
                      </button>
                      <button className={"btn btn-sm " + (viewMode === 'trends' ? 'btn-primary' : 'btn-outline-primary')} onClick={() => setViewMode('trends')}>
                          <i className="ti ti-trending-up me-1"></i> {t('Trends')}
                      </button>
                      <button className={"btn btn-sm " + (viewMode === 'analysis' ? 'btn-primary' : 'btn-outline-primary')} onClick={() => setViewMode('analysis')}>
                          <i className="ti ti-brain me-1"></i> {t('KI-Analyse')}
                      </button>
                      <button className={"btn btn-sm " + (viewMode === 'cards' ? 'btn-primary' : 'btn-outline-primary')} onClick={() => setViewMode('cards')}>
                          <i className="ti ti-layout-grid me-1"></i> {t('Karten')}
                      </button>
                  </div>
                  <Link to={'/businesses/'+params.locationId+'/competitors/add'} className="btn btn-success">{t('Add competitors')}</Link>
              </div>


              {viewMode === 'comparison' && businessProfile && competitors.length > 0 && (
              <>
                  <div className="row mt-3">
                      <div className="col-lg-6">
                          <div className="card rounded-2 shadow-sm border-0 shadow-sm">
                              <div className="card-body p-4">
                                  <h6 className="fw-semibold mb-3"><i className="ti ti-star me-2"></i>{t('Bewertungsvergleich')}</h6>
                                  {ratingChart && <Chart type="bar" options={ratingChart.options} series={ratingChart.series} height={chartHeight} />}
                              </div>
                          </div>
                      </div>
                      <div className="col-lg-6">
                          <div className="card rounded-2 shadow-sm border-0 shadow-sm">
                              <div className="card-body p-4">
                                  <h6 className="fw-semibold mb-3"><i className="ti ti-message-2 me-2"></i>{t('Bewertungen-Vergleich')}</h6>
                                  {reviewsChart && <Chart type="bar" options={reviewsChart.options} series={reviewsChart.series} height={chartHeight} />}
                              </div>
                          </div>
                      </div>
                  </div>

                  <div className="card rounded-2 shadow-sm border-0 shadow-sm mt-3">
                      <div className="card-body p-4">
                          <h6 className="fw-semibold mb-3"><i className="ti ti-table me-2"></i>{t('Detaillierter Vergleich')}</h6>
                          <div className="table-responsive">
                              <table className="table table-hover table-striped align-middle mb-0">
                                  <thead>
                                      <tr>
                                          <th scope="col">{t('Business')}</th>
                                          <th scope="col" className="text-center">{t('Rating')}</th>
                                          <th scope="col" className="text-center">{t('Reviews')}</th>
                                          <th scope="col">{t('Address')}</th>
                                          <th scope="col">{t('Website')}</th>
                                          <th scope="col">{t('Phone')}</th>
                                      </tr>
                                  </thead>
                                  <tbody>
                                      <tr className="table-primary">
                                          <td><div className="d-flex align-items-center gap-2"><i className="ti ti-star-filled text-warning"></i><strong>{getBusinessTitle()}</strong></div></td>
                                          <td className="text-center"><span className="badge bg-primary rounded-pill px-3 py-2 fs-3">{getBusinessRating().toFixed(1)}</span></td>
                                          <td className="text-center"><span className="fw-semibold">{getBusinessReviewCount()}</span></td>
                                          <td className="fs-2">{getBusinessAddress()}</td>
                                          <td className="fs-2">{getBusinessWebsite() ? <a href={getBusinessWebsite()} target="_blank" rel="noopener noreferrer">{(() => { try { return new URL(getBusinessWebsite()).hostname; } catch(e) { return getBusinessWebsite(); } })()}</a> : '-'}</td>
                                          <td className="fs-2">{getBusinessPhone() || '-'}</td>
                                      </tr>
                                      {competitors.map((competitor, index) => {
                                          const compRating = competitor.data?.rating || 0;
                                          const compReviews = competitor.data?.userRatingCount || 0;
                                          const myRating = getBusinessRating();
                                          const myReviews = getBusinessReviewCount();
                                          return (
                                          <tr key={'comp-row-'+index}>
                                              <td><div className="d-flex align-items-center gap-2"><span className="text-muted">#{index + 1}</span>
                                                  {competitor.data?.googleMapsUri ? <a href={competitor.data.googleMapsUri} target="_blank" rel="noopener noreferrer">{competitor.name || t('Unbekannt')}</a> : <span>{competitor.name || t('Unbekannt')}</span>}
                                              </div></td>
                                              <td className="text-center"><span className={"badge rounded-pill px-3 py-2 fs-3 " + (compRating >= myRating ? 'bg-danger' : 'bg-success')}>{compRating.toFixed(1)}</span></td>
                                              <td className="text-center"><span className={"fw-semibold " + (compReviews >= myReviews ? 'text-danger' : 'text-success')}>{compReviews}</span></td>
                                              <td className="fs-2">{competitor.data?.formattedAddress || '-'}</td>
                                              <td className="fs-2">{competitor.data?.websiteUri ? <a href={competitor.data.websiteUri} target="_blank" rel="noopener noreferrer">{(() => { try { return new URL(competitor.data.websiteUri).hostname; } catch(e) { return competitor.data.websiteUri; } })()}</a> : '-'}</td>
                                              <td className="fs-2">{competitor.data?.internationalPhoneNumber || '-'}</td>
                                          </tr>);
                                      })}
                                  </tbody>
                              </table>
                          </div>
                      </div>
                  </div>
              </>
              )}


              {viewMode === 'trends' && (
              <>
                  {trendsLoading && (
                      <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div><p className="mt-2 text-muted">{t('Trend-Daten werden geladen...')}</p></div>
                  )}

                  {!trendsLoading && trends.length > 0 && (
                  <div className="row mt-3">
                      <div className="col-lg-6">
                          <div className="card rounded-2 shadow-sm border-0 shadow-sm">
                              <div className="card-body p-4">
                                  <h6 className="fw-semibold mb-3"><i className="ti ti-star me-2"></i>{t('Bewertungsverlauf')}</h6>
                                  {trendsRatingChart ? <Chart type="line" options={trendsRatingChart.options} series={trendsRatingChart.series} height={350} /> : <p className="text-muted text-center py-4">{t('Noch nicht gen\u00fcgend Daten vorhanden. Der Verlauf wird t\u00e4glich aktualisiert.')}</p>}
                              </div>
                          </div>
                      </div>
                      <div className="col-lg-6">
                          <div className="card rounded-2 shadow-sm border-0 shadow-sm">
                              <div className="card-body p-4">
                                  <h6 className="fw-semibold mb-3"><i className="ti ti-message-2 me-2"></i>{t('Bewertungen-Verlauf')}</h6>
                                  {trendsReviewsChart ? <Chart type="line" options={trendsReviewsChart.options} series={trendsReviewsChart.series} height={350} /> : <p className="text-muted text-center py-4">{t('Noch nicht gen\u00fcgend Daten vorhanden. Der Verlauf wird t\u00e4glich aktualisiert.')}</p>}
                              </div>
                          </div>
                      </div>
                  </div>
                  )}

                  {!trendsLoading && trends.length > 0 && (
                  <div className="card rounded-2 shadow-sm border-0 shadow-sm mt-3">
                      <div className="card-body p-4">
                          <h6 className="fw-semibold mb-3"><i className="ti ti-list me-2"></i>{t('Aktuelle Werte')}</h6>
                          <div className="table-responsive">
                              <table className="table table-hover align-middle mb-0">
                                  <thead><tr><th>{t('Wettbewerber')}</th><th className="text-center">{t('Bewertung')}</th><th className="text-center">{t('Anzahl Bewertungen')}</th><th className="text-center">{t('Datenpunkte')}</th></tr></thead>
                                  <tbody>
                                      {trends.map((trend, i) => (
                                      <tr key={'trend-'+i}>
                                          <td className="fw-semibold">{trend.name}</td>
                                          <td className="text-center"><span className="badge bg-primary rounded-pill px-3 py-2">{(trend.currentRating || 0).toFixed(1)}</span></td>
                                          <td className="text-center">{trend.currentReviews || 0}</td>
                                          <td className="text-center"><span className="badge bg-light text-dark">{trend.ratingHistory?.length || 0}</span></td>
                                      </tr>
                                      ))}
                                  </tbody>
                              </table>
                          </div>
                      </div>
                  </div>
                  )}

                  {!trendsLoading && trends.length === 0 && (
                      <div className="card rounded-2 shadow-sm border-0 shadow-sm mt-3"><div className="card-body p-5 text-center text-muted">
                          <i className="ti ti-trending-up fs-1 d-block mb-3"></i>
                          <h5>{t('Noch keine Trend-Daten vorhanden')}</h5>
                          <p>{t('Die Daten werden t\u00e4glich automatisch erfasst. F\u00fcgen Sie zuerst Wettbewerber hinzu.')}</p>
                      </div></div>
                  )}
              </>
              )}


              {viewMode === 'analysis' && (
              <>
                  {!analysis && !analysisLoading && (
                      <div className="card rounded-2 shadow-sm border-0 shadow-sm mt-3"><div className="card-body p-5 text-center">
                          <i className="ti ti-brain fs-1 d-block mb-3 text-primary"></i>
                          <h5>{t('KI-gest\u00fctzte Wettbewerbsanalyse')}</h5>
                          <p className="text-muted mb-4">{t('Lassen Sie Ihre Wettbewerbsposition von unserer KI analysieren. Sie erhalten St\u00e4rken, Schw\u00e4chen, Chancen und konkrete Handlungsempfehlungen.')}</p>
                          <ButtonWithLoading className="btn-primary btn-lg mx-auto d-inline-flex align-items-center" noOriginalIconClassName={true} iconClassName="ti ti-sparkles me-2" label={t('Analyse starten')} loading={analysisLoading} onClick={loadAnalysis} disabled={competitors.length === 0} />
                          {competitors.length === 0 && <p className="text-muted mt-2 fs-2">{t('F\u00fcgen Sie zuerst Wettbewerber hinzu.')}</p>}
                      </div></div>
                  )}

                  {analysisLoading && (
                      <div className="card rounded-2 shadow-sm border-0 shadow-sm mt-3"><div className="card-body p-5 text-center">
                          <div className="spinner-border text-primary mb-3" role="status" style={{width: '3rem', height: '3rem'}}><span className="visually-hidden">Loading...</span></div>
                          <h5>{t('KI-Analyse l\u00e4uft...')}</h5>
                          <p className="text-muted">{t('Ihre Wettbewerbsdaten werden analysiert. Dies kann einige Sekunden dauern.')}</p>
                      </div></div>
                  )}

                  {analysis && !analysisLoading && (
                  <>
                      <div className="card rounded-2 shadow-sm mt-3 border-primary">
                          <div className="card-body p-4">
                              <h6 className="fw-semibold mb-3"><i className="ti ti-report-analytics me-2"></i>{t('Zusammenfassung')}</h6>
                              <p className="mb-0 fs-3">{analysis.summary}</p>
                          </div>
                      </div>

                      <div className="row mt-3">
                          <div className="col-lg-6">
                              <div className="card rounded-2 shadow-sm border-success">
                                  <div className="card-body p-4">
                                      <h6 className="fw-semibold mb-3 text-success"><i className="ti ti-thumb-up me-2"></i>{t('St\u00e4rken')}</h6>
                                      <ul className="mb-0">
                                          {(analysis.strengths || []).map((s, i) => <li key={'s-'+i} className="mb-2">{s}</li>)}
                                      </ul>
                                  </div>
                              </div>
                          </div>
                          <div className="col-lg-6">
                              <div className="card rounded-2 shadow-sm border-danger">
                                  <div className="card-body p-4">
                                      <h6 className="fw-semibold mb-3 text-danger"><i className="ti ti-thumb-down me-2"></i>{t('Schw\u00e4chen')}</h6>
                                      <ul className="mb-0">
                                          {(analysis.weaknesses || []).map((w, i) => <li key={'w-'+i} className="mb-2">{w}</li>)}
                                      </ul>
                                  </div>
                              </div>
                          </div>
                      </div>

                      <div className="row mt-3">
                          <div className="col-lg-6">
                              <div className="card rounded-2 shadow-sm border-info">
                                  <div className="card-body p-4">
                                      <h6 className="fw-semibold mb-3 text-info"><i className="ti ti-bulb me-2"></i>{t('Chancen')}</h6>
                                      <ul className="mb-0">
                                          {(analysis.opportunities || []).map((o, i) => <li key={'o-'+i} className="mb-2">{o}</li>)}
                                      </ul>
                                  </div>
                              </div>
                          </div>
                          <div className="col-lg-6">
                              <div className="card rounded-2 shadow-sm border-warning">
                                  <div className="card-body p-4">
                                      <h6 className="fw-semibold mb-3 text-warning"><i className="ti ti-target me-2"></i>{t('Empfehlungen')}</h6>
                                      <ol className="mb-0">
                                          {(analysis.recommendations || []).map((r, i) => <li key={'r-'+i} className="mb-2">{r}</li>)}
                                      </ol>
                                  </div>
                              </div>
                          </div>
                      </div>

                      {analysis.competitorInsights && analysis.competitorInsights.length > 0 && (
                      <div className="card rounded-2 shadow-sm border-0 shadow-sm mt-3">
                          <div className="card-body p-4">
                              <h6 className="fw-semibold mb-3"><i className="ti ti-users me-2"></i>{t('Wettbewerber-Einsch\u00e4tzung')}</h6>
                              <div className="table-responsive">
                                  <table className="table table-hover align-middle mb-0">
                                      <thead><tr><th>{t('Wettbewerber')}</th><th className="text-center">{t('Bedrohung')}</th><th>{t('Einsch\u00e4tzung')}</th></tr></thead>
                                      <tbody>
                                          {analysis.competitorInsights.map((ci, i) => (
                                          <tr key={'ci-'+i}>
                                              <td className="fw-semibold">{ci.name}</td>
                                              <td className="text-center"><span className={"badge bg-" + getThreatColor(ci.threat)}>{ci.threat}</span></td>
                                              <td>{ci.insight}</td>
                                          </tr>
                                          ))}
                                      </tbody>
                                  </table>
                              </div>
                          </div>
                      </div>
                      )}

                      <div className="text-center mt-3">
                          <ButtonWithLoading className="btn-outline-primary" noOriginalIconClassName={true} iconClassName="ti ti-refresh me-2" label={t('Analyse erneut durchf\u00fchren')} loading={analysisLoading} onClick={loadAnalysis} />
                      </div>
                  </>
                  )}
              </>
              )}


              {viewMode === 'comparison' && (!businessProfile || competitors.length === 0) && !loading && (
                  <div className="card rounded-2 shadow-sm border-0 shadow-sm mt-3"><div className="card-body p-5 text-center text-muted">
                      <i className="ti ti-chart-bar-off fs-1 d-block mb-3"></i>
                      <h5>{t('No competitors added yet')}</h5>
                      <p>{t('Add competitors to see how your business compares.')}</p>
                      <Link to={'/businesses/'+params.locationId+'/competitors/add'} className="btn btn-primary">{t('Add competitors')}</Link>
                  </div></div>
              )}


              {viewMode === 'cards' && (
              <>
                  <div className="row mt-3">
                      {competitors.map((competitor, index) => (
                          <div className={'col-lg-6 col-xxl-4'} key={'competitor-'+index}>
                              <BusinessCompetitorCard competitor={competitor} onDelete={onCompetitorDelete} />
                          </div>
                      ))}
                  </div>
                  {competitors.length === 0 && !loading && (
                      <div className="card rounded-2 shadow-sm border-0 shadow-sm mt-3"><div className="card-body p-5 text-center text-muted">
                          <h5>{t('No competitors added yet')}</h5>
                          <Link to={'/businesses/'+params.locationId+'/competitors/add'} className="btn btn-primary mt-2">{t('Add competitors')}</Link>
                      </div></div>
                  )}
              </>
              )}
          </>
      );
  }

  export default BusinessCompetitors;
  